<template>
  <div class="wrapper">
    <section v-if="conditionBody">
      <!-- <CustomScrollbar  auto> -->
      <div class="condition">
        <div class="condition__label">Si</div>

        <div class="condition__list">
          <header>
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="conditionBody.logical_type"
              class="select"
            >
              <a-select-option :value="LogicalType.Anyone">
                {{ TextByLogical[LogicalType.Anyone] }}
              </a-select-option>
              <a-select-option :value="LogicalType.Everyone">
                {{ TextByLogical[LogicalType.Everyone] }}
              </a-select-option>
            </a-select>

            <p>de las condiciones lógicas coinciden</p>
          </header>

          <IfCard
            v-for="ifElement in conditionBody.form_if"
            ref="ifCardsRef"
            :key="ifElement.id"
            :form-if="ifElement"
            :hide-delete="conditionBody.form_if.length === 1"
            :inputsList="formDetail?.form"
            @add="(id) => handleAdd('if', id)"
            @delete="(id) => handleDelete('if', id)"
            @update:form-if="(form) => handleUpdate('if', form)"
          />
        </div>
      </div>

      <a-divider class="divider" type="horizontal" />

      <div class="condition">
        <div class="condition__label">Entonces</div>

        <div class="condition__list">
          <header>
            <p>Realice las siguientes acciones</p>
          </header>

          <ElseCard
            v-for="elseElement in conditionBody.form_else"
            ref="elseCardsRef"
            :key="elseElement.id"
            :form-else="elseElement"
            :hide-delete="conditionBody.form_else.length === 1"
            :inputsList="formDetail?.form"
            @add="(id) => handleAdd('else', id)"
            @delete="(id) => handleDelete('else', id)"
            @update:form-else="(form) => handleUpdate('else', form)"
          />
        </div>
      </div>
      <!-- </CustomScrollbar> -->
    </section>

    <footer>
      <a-button @click="handleBack"> Volver </a-button>

      <a-button type="primary" :disabled="!hasChanges" @click="handleSave">
        Guardar condicional
      </a-button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { onMounted, inject, ref, computed, provide } from "vue";
import type { Ref } from "vue";
import { isEqual, cloneDeep } from "lodash";

import {
  Condition,
  ConditionElementType,
  Form,
  FormElse,
  FormIf,
  LogicalType,
} from "@/@types/entities/forms";
import { formConditionsManager } from "@/app/entities/utils/forms/FormConditionsManager";
import { TextByLogical } from "@/app/entities/utils/forms/conditionsText";

import IfCard from "@/app/entities/components/card/conditions/IfCard.vue";
import ElseCard from "@/app/entities/components/card/conditions/ElseCard.vue";
// import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

const props = defineProps<{
  currentCondition: string;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: "back"): void;
  (e: "save", condition: Condition): void;
}>();

const formDetail = inject("formDetail") as Ref<Form | undefined>;

const referenceBody = ref<Condition | undefined>(undefined);
const conditionBody = ref<Condition | undefined>(undefined);

provide("conditionBody", conditionBody);

onMounted(() => {
  const body = formConditionsManager.getConditionBody(props.currentCondition, formDetail.value);

  referenceBody.value = cloneDeep(body);
  conditionBody.value = body;
});

const hasChanges = computed(() => !isEqual(referenceBody.value, conditionBody.value));

const handleAdd = (type: ConditionElementType, id: string) => {
  if (!conditionBody.value) return;

  conditionBody.value = formConditionsManager.addElement(type, id, conditionBody.value);
};

const handleDelete = (type: ConditionElementType, id: string) => {
  if (!conditionBody.value) return;

  conditionBody.value = formConditionsManager.removeElement(type, id, conditionBody.value);
};

const handleUpdate = (type: ConditionElementType, form: FormIf | FormElse) => {
  if (!conditionBody.value) return;

  conditionBody.value = formConditionsManager.updateElement(type, form, conditionBody.value);
};

// Validate Fields
const ifCardsRef = ref<InstanceType<typeof IfCard>[]>([]);
const elseCardsRef = ref<InstanceType<typeof ElseCard>[]>([]);

const handleValidateFields = async () => {
  const ifCardsValidationPromises = ifCardsRef.value.map((ifForm) => ifForm.validate());
  const elseCardsValidationPromises = elseCardsRef.value.map((elseForm) => elseForm.validate());

  await Promise.all([...ifCardsValidationPromises, ...elseCardsValidationPromises]);
};

// Footer Actions
const handleBack = () => {
  emit("back");
};

const handleSave = async () => {
  handleValidateFields().then(() => {
    if (!conditionBody.value) return;

    emit("save", conditionBody.value);
  });
};
</script>

<style scoped lang="sass">
.wrapper
  display: flex
  flex-direction: column
  justify-content: space-between
  height: 100%

section
  max-height: calc(100vh - 270px - 1rem)
  overflow: auto

p
  margin: 0
  color: $gray-8

.select
  min-width: 40%

.condition
  display: grid
  grid-template-columns: 100px 1fr
  gap: 0 20px

  &__label
    border-radius: 2px
    background-color: $geekblue-5
    width: 100%
    height: 32px
    display: flex
    justify-content: center
    align-items: center
    color: $gray-9

  &__list
    display: grid
    grid-template-columns: 1fr
    gap: 12px 0

    header
      display: flex
      align-items: center
      height: 32px
      gap: 8px

.divider
  background-color: $geekblue-5
  margin: 20px 0

footer
  display: flex
  align-items: center
  justify-content: flex-end
  gap: 0 12px
  padding: 12px 0
  border-top: 1px solid $gray-5
</style>
